/**
 * Monaco Editor 本地化配置
 *
 * 提供多语言支持，包括中文界面
 */

import { useState } from 'react'

/**
 * 支持的语言列表
 */
export const SUPPORTED_LOCALES = {
  'zh-cn': '中文（简体）',
  'zh-tw': '中文（繁體）',
  en: 'English',
  ja: '日本語',
  ko: '한국어',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  'pt-br': 'Português (Brasil)',
  ru: 'Русский',
} as const

export type SupportedLocale = keyof typeof SUPPORTED_LOCALES

/**
 * Monaco Editor 中文本地化配置
 */
const zhCnLocalization = {
  'vs/editor/contrib/find/FindWidget.title': '查找',
  'vs/editor/contrib/find/FindWidget.placeholder': '查找',
  'vs/editor/contrib/find/FindWidget.previousMatch.label': '上一个',
  'vs/editor/contrib/find/FindWidget.nextMatch.label': '下一个',
  'vs/editor/contrib/find/FindWidget.toggleSelectionFind.label': '在选区中查找',
  'vs/editor/contrib/find/FindWidget.closeFindWidget.label': '关闭',
  'vs/editor/contrib/find/FindWidget.replace.label': '替换',
  'vs/editor/contrib/find/FindWidget.replaceAll.label': '全部替换',
  'vs/editor/contrib/find/FindWidget.matchCase.title': '区分大小写',
  'vs/editor/contrib/find/FindWidget.wholeWord.title': '全字匹配',
  'vs/editor/contrib/find/FindWidget.regExp.title': '使用正则表达式',
  'vs/editor/contrib/find/FindWidget.inputPlaceholders.replace': '输入以替换结果',
  'vs/editor/contrib/find/FindWidget.notFoundResult': '未找到结果',
  'vs/editor/contrib/find/FindWidget.resultCount': '结果 {0}',
  'vs/editor/contrib/find/FindWidget.resultCountMultiple': '结果 {0} of {1}',
  'vs/editor/contrib/find/FindWidget.inputPlaceholders.result': '输入以搜索结果',
  'vs/editor/contrib/find/FindWidget.searchScope.title': '当前范围',
  'vs/editor/contrib/find/FindWidget.searchScope.currentFile': '当前文件',
  'vs/editor/contrib/find/FindWidget.searchScope.openEditors': '打开的编辑器',
  'vs/editor/contrib/find/FindWidget.replaceDisabledMessage': '请输入要替换的内容',
  'vs/editor/contrib/find/FindWidget.replaceAllSuccessMessage': '已替换 {0} 个匹配项',
  'vs/editor/contrib/find/FindWidget.replaceAllFailedMessage': '替换失败',
  'vs/editor/contrib/find/FindWidget.searchScope.allFiles': '所有文件',
  'vs/editor/contrib/contextMenu.cut.label': '剪切',
  'vs/editor/contrib/contextMenu.copy.label': '复制',
  'vs/editor/contrib/contextMenu.paste.label': '粘贴',
  'vs/editor/contrib/contextMenu.selectAll.label': '全选',
  'vs/editor/contrib/contextMenu.undo.label': '撤销',
  'vs/editor/contrib/contextMenu.redo.label': '重做',
  'vs/editor/contrib/contextMenu.find.label': '查找',
  'vs/editor/contrib/contextMenu.replace.label': '替换',
  'vs/editor/contrib/contextMenu.findInSelection.label': '在选区中查找',
  'vs/editor/contrib/contextMenu.addSelectionForNextFindMatch.label': '添加下一个匹配项到选区',
  'vs/editor/contrib/contextMenu.addSelectionForPreviousFindMatch.label': '添加上一个匹配项到选区',
  'vs/editor/contrib/contextMenu.moveSelectionToNextFindMatch.label': '移动选区到下一个匹配项',
  'vs/editor/contrib/contextMenu.moveSelectionToPreviousFindMatch.label': '移动选区到上一个匹配项',
  'vs/editor/contrib/contextMenu.copyLine.label': '复制行',
  'vs/editor/contrib/contextMenu.copyLineDown.label': '复制行并插入到下一行',
  'vs/editor/contrib/contextMenu.copyLineUp.label': '复制行并插入到上一行',
  'vs/editor/contrib/contextMenu.cutLine.label': '剪切行',
  'vs/editor/contrib/contextMenu.deleteLine.label': '删除行',
  'vs/editor/contrib/contextMenu.insertLineBefore.label': '在前面插入行',
  'vs/editor/contrib/contextMenu.insertLineAfter.label': '在后面插入行',
  'vs/editor/contrib/contextMenu.transpose.label': '交换行',
  'vs/editor/contrib/contextMenu.duplicateSelection.label': '重复选区',
  'vs/editor/contrib/contextMenu.indentLines.label': '缩进行',
  'vs/editor/contrib/contextMenu.outdentLines.label': '取消缩进行',
  'vs/editor/contrib/contextMenu.commentLine.label': '添加行注释',
  'vs/editor/contrib/contextMenu.uncommentLine.label': '移除行注释',
  'vs/editor/contrib/contextMenu.toggleLineComment.label': '切换行注释',
  'vs/editor/contrib/contextMenu.toggleBlockComment.label': '切换块注释',
  'vs/editor/contrib/contextMenu.quickFix.label': '快速修复',
  'vs/editor/contrib/contextMenu.refactor.label': '重构',
  'vs/editor/contrib/contextMenu.showHover.label': '显示悬停信息',
  'vs/editor/contrib/contextMenu.revealType.label': '显示类型定义',
  'vs/editor/contrib/contextMenu.peekDefinition.label': '预览定义',
  'vs/editor/contrib/contextMenu.peekDeclaration.label': '预览声明',
  'vs/editor/contrib/contextMenu.peekImplementation.label': '预览实现',
  'vs/editor/contrib/contextMenu.peekTypeDefinition.label': '预览类型定义',
  'vs/editor/contrib/contextMenu.gotoDefinition.label': '转到定义',
  'vs/editor/contrib/contextMenu.gotoDeclaration.label': '转到声明',
  'vs/editor/contrib/contextMenu.gotoImplementation.label': '转到实现',
  'vs/editor/contrib/contextMenu.gotoTypeDefinition.label': '转到类型定义',
  'vs/editor/contrib/contextMenu.findReferences.label': '查找引用',
  'vs/editor/contrib/contextMenu.rename.label': '重命名符号',
  'vs/editor/contrib/contextMenu.changeLogLevel.label': '更改日志级别',
  'vs/editor/contrib/contextMenu.commandPalette.label': '命令面板',
  'vs/editor/contrib/contextMenu.format.label': '格式化文档',
  'vs/editor/contrib/contextMenu.formatSelection.label': '格式化选区',
  'vs/editor/contrib/contextMenu.split.label': '拆分编辑器',
  'vs/editor/contrib/contextMenu.closeEditor.label': '关闭编辑器',
  'vs/editor/contrib/contextMenu.groupEditors.label': '组合编辑器',
  'vs/editor/contrib/contextMenu.showEditor.label': '显示编辑器',
  'vs/editor/contrib/contextMenu.copyPath.label': '复制路径',
  'vs/editor/contrib/contextMenu.relativePath.label': '复制相对路径',
  'vs/editor/contrib/contextMenu.saveAs.label': '另存为...',
  'vs/editor/contrib/contextMenu.saveAll.label': '全部保存',
  'vs/editor/contrib/contextMenu.save.label': '保存',
  'vs/editor/contrib/contextMenu.newFile.label': '新建文件',
  'vs/editor/contrib/contextMenu.revealFileInOS.label': '在操作系统中显示',
  'vs/editor/contrib/contextMenu.copyFileName.label': '复制文件名',
  'vs/editor/contrib/contextMenu.copyRelativePath.label': '复制相对路径',
  'vs/editor/contrib/contextMenu.openContainingFolder.label': '打开包含文件夹',
  'vs/editor/contrib/contextMenu.compareWithSelected.label': '与选定的进行比较',
  'vs/editor/contrib/contextMenu.selectForCompare.label': '选择用于比较',
  'vs/editor/contrib/contextMenu.revealInSideBySideEditor.label': '在并排编辑器中显示',
  'vs/editor/contrib/contextMenu.openToSideToRight.label': '向右侧打开',
  'vs/editor/contrib/contextMenu.openToSide.label': '在侧边打开',
  'vs/editor/contrib/contextMenu.openToSideLeft.label': '向左侧打开',
  'vs/editor/contrib/contextMenu.openToSideDown.label': '向下打开',
  'vs/editor/contrib/contextMenu.openToSideUp.label': '向上打开',
  'vs/editor/contrib/contextMenu.addNotebookOutput.label': '添加Notebook输出',
  'vs/editor/contrib/contextMenu.addCommentThread.label': '添加注释线程',
  'vs/editor/contrib/contextMenu.collapseUnfoldRegions.label': '折叠/展开区域',
  'vs/editor/contrib/contextMenu.addFolder.label': '添加文件夹',
  'vs/editor/contrib/contextMenu.addFile.label': '添加文件',
  'vs/editor/contrib/contextMenu.addRootFolder.label': '添加根文件夹',
  'vs/editor/contrib/contextMenu.revealInExplorer.label': '在资源管理器中显示',
}

const zhTwLocalization = {
  ...zhCnLocalization,
  'vs/editor/contrib/find/FindWidget.title': '尋找',
  'vs/editor/contrib/find/FindWidget.placeholder': '尋找',
  'vs/editor/contrib/contextMenu.cut.label': '剪下',
  'vs/editor/contrib/contextMenu.copy.label': '複製',
  'vs/editor/contrib/contextMenu.paste.label': '貼上',
  'vs/editor/contrib/contextMenu.selectAll.label': '全選',
  'vs/editor/contrib/contextMenu.undo.label': '復原',
  'vs/editor/contrib/contextMenu.redo.label': '重做',
}

/**
 * 配置Monaco Editor本地化
 */
export const configureMonacoLocalization = (locale: SupportedLocale = 'zh-cn') => {
  // Monaco Editor 的本地化主要通过在beforeMount中设置
  // 这里我们准备本地化数据，实际配置在组件的beforeMount中进行
  return {
    locale,
    localization: locale === 'zh-tw' ? zhTwLocalization : zhCnLocalization,
  }
}

/**
 * 获取当前用户的语言偏好
 */
export const getUserLocale = (): SupportedLocale => {
  // 从浏览器获取语言设置
  const browserLanguage = navigator.language || (navigator as { userLanguage?: string }).userLanguage

  // 检测中文
  if (browserLanguage?.startsWith('zh')) {
    if (browserLanguage.includes('TW') || browserLanguage.includes('HK') || browserLanguage.includes('MO')) {
      return 'zh-tw'
    }
    return 'zh-cn'
  }

  // 检测其他支持的语言
  const localeCode = browserLanguage?.split('-')[0]
  if (localeCode in SUPPORTED_LOCALES) {
    return localeCode as SupportedLocale
  }

  return 'zh-cn' // 默认中文简体
}

/**
 * Monaco Editor本地化Hook
 */
export const useMonacoLocalization = () => {
  const [locale, setLocale] = useState<SupportedLocale>(() => getUserLocale())

  const switchLocale = (newLocale: SupportedLocale) => {
    setLocale(newLocale)
  }

  return {
    locale,
    setLocale: switchLocale,
    availableLocales: Object.entries(SUPPORTED_LOCALES).map(([code, name]) => ({
      code,
      name,
    })),
  }
}
